/**
 * How this product writes a moment in time.
 *
 * One implementation, shared. It lived in `features/workspace-files/format.ts`
 * first, and its docstring there recorded why it exists at all: the file listing
 * said "2h ago" while the history entry three inches to its right said
 * "Mar 12, 2026, 4:05 PM" about the same event. The secrets page then became a
 * third surface with a fourth opinion, which is the point at which a formatter
 * stops belonging to one feature.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * A parsed moment and how long ago it was, or `null` when there is nothing to
 * measure — no value, or a string that is not a date.
 *
 * The one place this module parses a contract timestamp: every formatter below
 * goes through here, so none of them can disagree with another about whether a
 * given string is a moment at all.
 *
 * A *present* value that does not parse is reported once per distinct value.
 * An absent one is not: `null` is a legal contract value on several of these
 * fields, so warning about it would bury the case that is a real
 * data-consistency defect under the case that is normal.
 */
function momentOf(
  at: string | null | undefined,
): { date: Date; elapsed: number } | null {
  if (!at) return null;
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) {
    reportUnparsable(at);
    return null;
  }
  return { date, elapsed: Date.now() - date.getTime() };
}

/**
 * Values already reported, so a malformed timestamp on a row that re-renders
 * (a virtualised list re-measures constantly) is one line in the console
 * rather than a stream of them.
 */
const reportedUnparsable = new Set<string>();

function reportUnparsable(at: string): void {
  if (reportedUnparsable.has(at)) return;
  reportedUnparsable.add(at);
  console.warn(
    `Unparsable timestamp from the API, rendered as "—": ${JSON.stringify(at)}`,
  );
}

/**
 * What the formatters that render a moment as text — {@link formatRelativeTime},
 * {@link formatElapsedSince} and {@link absoluteDateTime} — print when there is
 * no moment to print. Not used by {@link formatExactTime}, which produces a
 * `title` and so has an empty string rather than a dash to fall back to, nor by
 * {@link formatFreshness}, which takes a number and never has nothing.
 */
const NO_MOMENT = '—';

/**
 * Coarse "2h ago".
 *
 * Deliberately imprecise: the question a reader asks a timestamp column is
 * usually "recently, or ages ago?", and an exact clock time makes them do
 * arithmetic to answer it. The precise value is not lost — put
 * {@link formatExactTime} on the element's `title`.
 */
export function formatRelativeTime(at: string | null | undefined): string {
  const moment = momentOf(at);
  if (!moment) return NO_MOMENT;
  const { date, elapsed } = moment;
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) return `${String(Math.floor(elapsed / MINUTE))}m ago`;
  if (elapsed < DAY) return `${String(Math.floor(elapsed / HOUR))}h ago`;
  if (elapsed < 30 * DAY) return `${String(Math.floor(elapsed / DAY))}d ago`;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * How long it has been, as a bare duration — "3d", "2h", "5m".
 *
 * The same coarseness as {@link formatRelativeTime} and deliberately without
 * its "ago": this answers "how long has this been going on", which reads as a
 * span, where "ago" reads as a point. A board asking how long a task has sat
 * in a step wants the span.
 */
export function formatElapsedSince(at: string | null | undefined): string {
  const moment = momentOf(at);
  if (!moment) return NO_MOMENT;
  return formatSpan(moment.elapsed);
}

/**
 * How long it has been, in words: "3 hours", "4 days", "2 weeks" (spec 152
 * FR-006).
 *
 * For a sentence a person reads, "1 day on this step", where the board's
 * compact "1d" makes them decode a unit. Coarse on purpose: under an hour is
 * one phrase, hours until a day, days until two weeks, then whole weeks.
 * Empty when the time will not parse, so the caller leaves the phrase out.
 */
export function formatDurationWords(
  at: string | null | undefined,
  now: number = Date.now(),
): string {
  const then = at ? Date.parse(at) : Number.NaN;
  if (Number.isNaN(then)) return '';
  const ms = Math.max(0, now - then);
  if (ms < HOUR) return 'less than an hour';
  if (ms < DAY) return countOf(Math.floor(ms / HOUR), 'hour');
  const days = Math.floor(ms / DAY);
  if (days < 14) return countOf(days, 'day');
  return countOf(Math.floor(days / 7), 'week');
}

function countOf(n: number, unit: string): string {
  return `${String(n)} ${unit}${n === 1 ? '' : 's'}`;
}

/**
 * A span of milliseconds as "3d", "2h", "5m" — the coarseness of
 * {@link formatElapsedSince}, for spans that do not end now.
 *
 * A task's lead time runs from creation to when it finished, and a median of
 * several lead times ends nowhere at all; both have to read in the same units
 * as a span that ends now, or one page would say "3h" and "3h 12m" about the
 * same kind of thing.
 */
export function formatSpan(ms: number): string {
  // A negative span is a clock disagreeing with the server, not a task from
  // the future. Reported as the smallest span rather than as "-3m".
  if (ms < MINUTE) return '<1m';
  if (ms < HOUR) return `${String(Math.floor(ms / MINUTE))}m`;
  if (ms < DAY) return `${String(Math.floor(ms / HOUR))}h`;
  return `${String(Math.floor(ms / DAY))}d`;
}

/**
 * Hoisted, not built per call: {@link absoluteDateTime} is called once per
 * visible row of a virtualised list on every render, and constructing an
 * `Intl.DateTimeFormat` is the expensive part. The repo hoists its other
 * formatters for the same reason (`meterFooter.ts`, `executionHistory.ts`).
 */
const ABSOLUTE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** A moment as both reads a `<time>` element needs. */
export interface AbsoluteMoment {
  /** The visible text, or `—` when there is no moment to print. */
  text: string;
  /**
   * The contract value, verbatim, when it parsed — else absent. Spec 130
   * FR-011 omits `<time dateTime>` entirely rather than setting it to an
   * unparsable string, and an absent property is exactly what React needs to
   * omit the attribute.
   */
  machine?: string;
}

/**
 * The moment itself, written out — e.g. `19/08/2026, 09:12` under `en-GB`;
 * field order and separators vary by locale, so do not read that example as
 * the output shape.
 *
 * Spec 130 FR-010/FR-011. Where {@link formatRelativeTime} answers "recently,
 * or ages ago?", this answers "when exactly", on the line rather than on a
 * `title` — a comment timeline is a record, and a record that only says
 * "2h ago" cannot be read a week later.
 *
 * Returns the text *and* the machine-readable value together rather than
 * leaving a caller to ask a second predicate: both answers come from the one
 * parse, so a `dateTime` cannot end up disagreeing with the text beside it,
 * and a row parses its timestamp once.
 *
 * Day and month are two-digit and the hour is `hourCycle: 'h23'` rather than
 * `hour12: false`: the latter is honoured inconsistently and yields `24:05`
 * for five past midnight in some runtimes, where `h23` always gives `00:05`.
 *
 * The locale argument is left unspecified on purpose. What the options fix is
 * the field *set* and each field's width — day, month, numeric year, 24-hour
 * hour, minute, no seconds. They do not fix the field *order* or the
 * separators: `Intl` derives both from the reader's locale, so the same call
 * yields `19/08/2026, 09:12` under `en-GB` and `08/19/2026, 09:12` under
 * `en-US`. That is accepted — a reader sees their own convention — with the
 * known consequence that a day/month-ambiguous date (`05/06/2026`) cannot be
 * disambiguated across locales from the visible text alone. Callers that need
 * the exact value use `machine`.
 */
export function absoluteDateTime(
  at: string | null | undefined,
): AbsoluteMoment {
  const moment = momentOf(at);
  if (!moment || !at) return { text: NO_MOMENT };
  return { text: ABSOLUTE_FORMAT.format(moment.date), machine: at };
}

/** The exact value, for the `title` the relative label would otherwise hide. */
export function formatExactTime(at: string | null | undefined): string {
  const moment = momentOf(at);
  return moment ? moment.date.toLocaleString() : '';
}

/**
 * How long ago a page last read its data, in the words its status pill uses
 * (spec 126 FR-007). Coarse on purpose: tens of seconds below a minute and
 * whole minutes after, so the text changes a few times a minute rather than
 * every second.
 */
export function formatFreshness(updatedAt: number, now: number): string {
  const seconds = Math.floor((now - updatedAt) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${String(Math.floor(seconds / 10) * 10)} s ago`;
  return `${String(Math.floor(seconds / 60))} min ago`;
}
